/**
 * Home's two choice cards (TMMAPP-0152 §A 4–5; P:94–130). Each is a radio group named by its
 * heading, built on native radios so the arrow keys move the pick; the choice lives in this visit's
 * `sessionStorage` only (`homeRules.ts`) and is never sent.
 *
 * - **How are you feeling about money today?** Five faces (`MOOD_FACES`, 34 px) with their labels.
 *   The picked face sits on the `hover` wash with a `line` border (P:103). Its note shows under the row
 *   and, when the note's intent isn't the current one, a pill switches to it. "Optional. Skip it any
 *   time." closes the card.
 * - **What do you need from money today?** Four tiles, 2 × 2: the current one is `brand` with
 *   `brandInk` inside a `brandBoundary` border, the others `surface2` with a `line` border (hover
 *   `brandBoundary`). The mood's switch pill has a `brandBoundary` border too.
 */
import { useId, type ReactNode } from 'react';
import { Box, ButtonBase, Paper, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { MOOD_FACES, moodFaceColours, type Mood } from '@/theme/categorical';
import { PILL_RADIUS, resolveTokens } from '@/theme';
import { INTENTS, MOOD_INTENT, type Intent } from './homeRules';

/** The prototype's face (P:834): a filled circle, two eyes and a mouth curved by the mood. */
const MOUTH_CURVE: Record<Mood, number> = {
  stressed: -4,
  overwhelmed: -3,
  okay: 0,
  good: 3,
  confident: 5,
};

function MoodFace({ mood }: { mood: Mood }) {
  const { fill, features } = moodFaceColours(mood, useTheme().palette.mode);
  const curve = MOUTH_CURVE[mood];
  return (
    <svg width={34} height={34} viewBox="0 0 34 34" aria-hidden focusable="false">
      <circle cx={17} cy={17} r={16} fill={fill} />
      <circle cx={12} cy={14} r={1.8} fill={features} />
      <circle cx={22} cy={14} r={1.8} fill={features} />
      <path
        d={`M11 ${22 - curve / 2} Q17 ${22 + curve} 23 ${22 - curve / 2}`}
        stroke={features}
        strokeWidth={1.8}
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** One native radio in a styled label; the label's whole box is the target. */
function Choice({
  name,
  checked,
  onPick,
  sx,
  children,
}: {
  name: string;
  checked: boolean;
  onPick: () => void;
  sx: object;
  children: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="label"
      data-checked={checked || undefined}
      sx={{
        cursor: 'pointer',
        '&:has(input:focus-visible)': {
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: 2,
        },
        ...sx,
      }}
    >
      <input type="radio" name={name} checked={checked} onChange={onPick} style={visuallyHidden} />
      {children}
    </Box>
  );
}

function ChoiceCard({
  title,
  titleId,
  children,
}: {
  title: string;
  titleId: string;
  children: ReactNode;
}) {
  return (
    <Paper sx={{ p: '20px 22px', borderRadius: '20px', display: 'flex', flexDirection: 'column' }}>
      <Typography id={titleId} variant="h4" component="h2">
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export function MoodCard({
  mood,
  intent,
  onMood,
  onIntent,
}: {
  mood: Mood | null;
  intent: Intent;
  onMood: (mood: Mood) => void;
  onIntent: (intent: Intent) => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const titleId = useId();
  const name = useId();
  const offer = mood === null ? null : MOOD_INTENT[mood];
  return (
    <ChoiceCard title={t('home.mood.title')} titleId={titleId}>
      <Box
        role="radiogroup"
        aria-labelledby={titleId}
        data-moods=""
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: '6px',
          justifyContent: 'space-between',
          mt: '14px',
        }}
      >
        {MOOD_FACES.map(({ mood: face }) => (
          <Choice
            key={face}
            name={name}
            checked={mood === face}
            onPick={() => onMood(face)}
            sx={{
              flex: { xs: '1 0 30%', sm: 1 },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '6px',
              p: '8px 2px',
              borderRadius: '14px',
              border: '1px solid',
              // The picked face (P:103): the `hover` wash with a `line` border (TMMAPP-0154 §A 5).
              borderColor: mood === face ? tokens.line : 'transparent',
              bgcolor: mood === face ? tokens.hover : 'transparent',
              color: tokens.text2,
              fontSize: 12,
              lineHeight: '16px',
              textAlign: 'center',
              overflowWrap: 'anywhere',
              '&:hover': { bgcolor: tokens.hover },
            }}
          >
            <MoodFace mood={face} />
            {t(`home.mood.${face}`)}
          </Choice>
        ))}
      </Box>
      {mood !== null && offer !== null && (
        <Box
          data-mood-note=""
          sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', mt: '14px' }}
        >
          <Typography
            role="status"
            component="span"
            sx={{ flex: '1 1 200px', fontSize: 14, lineHeight: '20px', color: tokens.text2 }}
          >
            {t(`home.mood.note.${mood}`)}
          </Typography>
          {offer !== intent && (
            <ButtonBase
              onClick={() => onIntent(offer)}
              data-mood-switch=""
              sx={{
                p: '7px 12px',
                borderRadius: `${PILL_RADIUS}px`,
                border: '1px solid',
                borderColor: tokens.brandBoundary,
                color: tokens.link,
                fontWeight: 600,
                fontSize: 13,
                lineHeight: '18px',
                '&:hover': { bgcolor: tokens.hover },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${tokens.focusRing}`,
                  outlineOffset: 2,
                },
              }}
            >
              {t(`home.mood.switch.${offer}`)}
            </ButtonBase>
          )}
        </Box>
      )}
      <Typography
        component="p"
        sx={{
          m: 0,
          mt: '14px',
          fontSize: 12,
          lineHeight: '17px',
          color: tokens.textLabel('surface'),
        }}
      >
        {t('home.mood.optional')}
      </Typography>
    </ChoiceCard>
  );
}

export function IntentCard({
  intent,
  onIntent,
}: {
  intent: Intent;
  onIntent: (intent: Intent) => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const titleId = useId();
  const name = useId();
  return (
    <ChoiceCard title={t('home.intent.title')} titleId={titleId}>
      <Box
        role="radiogroup"
        aria-labelledby={titleId}
        data-intents=""
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: '12px' }}
      >
        {INTENTS.map((id) => {
          const on = id === intent;
          return (
            <Choice
              key={id}
              name={name}
              checked={on}
              onPick={() => onIntent(id)}
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                p: '10px 12px',
                borderRadius: '14px',
                border: '1px solid',
                borderColor: on ? tokens.brandBoundary : tokens.line,
                bgcolor: on ? tokens.brand : tokens.surface2,
                color: on ? tokens.brandInk : tokens.text,
                textAlign: 'start',
                overflowWrap: 'anywhere',
                '&:hover': { borderColor: tokens.brandBoundary },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, fontSize: 14, lineHeight: '20px' }}>
                {t(`home.intent.${id}.label`)}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  lineHeight: '17px',
                  color: on ? tokens.brandInk : tokens.text2,
                }}
              >
                {t(`home.intent.${id}.sub`)}
              </Box>
            </Choice>
          );
        })}
      </Box>
    </ChoiceCard>
  );
}
